import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { 
  getSafetyCategories, 
  saveSafetyCategory, 
  deleteSafetyCategory,
  getRiskCauses,
  saveRiskCause,
  deleteRiskCause
} from '../../../api/safetyAssessApi';
import '../Safety.css';

function SafetyHazardCategory({ comCode = '' }) {
  const { token } = useContext(AuthContext);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [causes, setCauses] = useState([]);

  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState({ name: '', orderNum: 99 });
  const [newCause, setNewCause] = useState({ name: '', orderNum: 99 });

  // 인라인 편집 상태
  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  const [editingCauseId, setEditingCauseId] = useState(null);
  const [editCauseName, setEditCauseName] = useState('');

  const fetchCategories = useCallback(async () => {
    if (!token || !comCode) return;
    try {
      const data = await getSafetyCategories(token, comCode);
      setCategories(data || []);
      if (data && data.length > 0 && !selectedCategory) {
        setSelectedCategory(data[0]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, comCode, selectedCategory]);

  const fetchCauses = useCallback(async (categoryId) => {
    if (!token || !categoryId || !comCode) return;
    try {
      const data = await getRiskCauses(categoryId, token, comCode);
      setCauses(data || []);
    } catch (err) {
      console.error(err);
    }
  }, [token, comCode]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  useEffect(() => {
    if (selectedCategory) {
      fetchCauses(selectedCategory.id);
    }
  }, [selectedCategory, fetchCauses]);

  const handleSelectCategory = (cat) => {
    setSelectedCategory(cat);
    setEditingCatId(null);
    setEditingCauseId(null);
  };

  const handleSaveCategory = async (cat) => {
    if (!cat.name) return alert('대분류명을 입력하세요.');
    try {
      await saveSafetyCategory(cat, token, comCode);
      await fetchCategories();
      setNewCat({ name: '', orderNum: 99 });
    } catch (err) {
      alert('저장 실패');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('선택한 대분류를 삭제하시겠습니까?\n하위 세부 원인 항목도 모두 삭제됩니다.')) return;
    try {
      await deleteSafetyCategory(id, token, comCode);
      if (selectedCategory && selectedCategory.id === id) {
        setSelectedCategory(null);
        setCauses([]);
      }
      await fetchCategories();
    } catch (err) {
      alert('삭제 실패');
    }
  };

  // 대분류 인라인 수정
  const handleStartEditCat = (cat, e) => {
    e.stopPropagation();
    setEditingCatId(cat.id);
    setEditCatName(cat.name);
  };

  const handleConfirmEditCat = async () => {
    if (!editCatName.trim()) return;
    try {
      await saveSafetyCategory({ id: editingCatId, name: editCatName.trim() }, token, comCode);
      setEditingCatId(null);
      await fetchCategories();
    } catch (err) {
      alert('수정 실패');
    }
  };

  const handleSaveCause = async (cause) => {
    if (!cause.name) return alert('요인명을 입력하세요.');
    if (!selectedCategory) return alert('대분류를 먼저 선택하세요.');
    try {
      await saveRiskCause({ ...cause, categoryId: selectedCategory.id }, token, comCode);
      await fetchCauses(selectedCategory.id);
      setNewCause({ name: '', orderNum: 99 });
    } catch (err) {
      alert('저장 실패');
    }
  };

  const handleDeleteCause = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await deleteRiskCause(id, token, comCode);
      if (selectedCategory) await fetchCauses(selectedCategory.id);
    } catch (err) {
      alert('삭제 실패');
    }
  };

  // 소분류 인라인 수정
  const handleStartEditCause = (cause) => {
    setEditingCauseId(cause.id);
    setEditCauseName(cause.name);
  };

  const handleConfirmEditCause = async () => {
    if (!editCauseName.trim()) return;
    try {
      await saveRiskCause({ id: editingCauseId, name: editCauseName.trim(), categoryId: selectedCategory.id }, token, comCode);
      setEditingCauseId(null);
      await fetchCauses(selectedCategory.id);
    } catch (err) {
      alert('수정 실패');
    }
  };

  if (loading) {
    return (
      <div className="sf-page">
        <div className="sf-loading">
          <div className="sf-spinner"></div>
          <span className="sf-loading-text">분류 체계를 불러오는 중입니다...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ animation: 'fadeIn 0.2s ease-in-out' }}>

      {/* 2열 레이아웃 */}
      <div className="row g-4">
        {/* 좌측: 대분류 목록 */}
        <div className="col-md-5">
          <div className="sf-card" style={{height: '100%'}}>
            <div className="sf-card-header sf-gradient-primary">
              <h5 className="sf-card-header-title">
                <i className="bi bi-folder2-open"></i> 위험요인 대분류
              </h5>
              <span className="sf-badge sf-badge-white">{categories.length}개</span>
            </div>
            <div className="sf-card-body">
              {/* 신규 입력 */}
              <div className="sf-input-group">
                <input
                  className="sf-input"
                  type="text"
                  placeholder="신규 대분류명 (예: 7. 기타 요인)"
                  value={newCat.name}
                  onChange={e => setNewCat({ ...newCat, name: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && handleSaveCategory(newCat)}
                />
                <button
                  className="sf-btn sf-btn-primary sf-btn-sm"
                  onClick={() => handleSaveCategory(newCat)}
                  style={{whiteSpace: 'nowrap'}}
                >
                  <i className="bi bi-plus-lg"></i> 등록
                </button>
              </div>

              {/* 대분류 목록 */}
              <div>
                {categories.map((cat) => {
                  const isSelected = selectedCategory && selectedCategory.id === cat.id;
                  const isEditing = editingCatId === cat.id;
                  return (
                    <div
                      key={cat.id}
                      className={`sf-list-item ${(isSelected && !isEditing) ? 'active' : ''}`}
                      onClick={() => handleSelectCategory(cat)}
                    >
                      {isEditing ? (
                        <div className="d-flex align-items-center gap-2" style={{flex: 1}} onClick={e => e.stopPropagation()}>
                          <input
                            className="sf-input"
                            type="text"
                            value={editCatName}
                            onChange={e => setEditCatName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleConfirmEditCat()}
                            autoFocus
                            style={{padding: '6px 10px', fontSize: '0.88rem'}}
                          />
                          <button className="sf-btn sf-btn-primary sf-btn-sm" onClick={handleConfirmEditCat} style={{borderRadius: 6, height: 28, padding: '0 10px', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap'}}>
                            저장
                          </button>
                          <button className="sf-btn sf-btn-sm" onClick={() => setEditingCatId(null)} style={{borderRadius: 6, height: 28, padding: '0 10px', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1'}}>
                            취소
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="sf-list-item-label" title={cat.name} onDoubleClick={(e) => handleStartEditCat(cat, e)} style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '10px', cursor: 'pointer' }}>{cat.name}</span>
                          <div className="sf-list-item-actions" style={{display: 'flex', gap: 6, alignItems: 'center'}}>
                            <button
                              className="sf-btn sf-btn-sm"
                              onClick={(e) => handleStartEditCat(cat, e)}
                              style={isSelected ? {background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '4px 10px', fontSize: '0.75rem', height: 24, borderRadius: 4, fontWeight: 700} : {background: '#eff6ff', border: 'none', color: '#2563eb', padding: '4px 10px', fontSize: '0.75rem', height: 24, borderRadius: 4, fontWeight: 700}}
                            >
                              수정
                            </button>
                            <button
                              className="sf-btn sf-btn-sm"
                              onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}
                              style={isSelected ? {background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '4px 10px', fontSize: '0.75rem', height: 24, borderRadius: 4, fontWeight: 700} : {background: '#fef2f2', border: 'none', color: '#ef4444', padding: '4px 10px', fontSize: '0.75rem', height: 24, borderRadius: 4, fontWeight: 700}}
                            >
                              삭제
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* 우측: 세부 원인(소분류) */}
        <div className="col-md-7">
          <div className="sf-card" style={{height: '100%'}}>
            <div className="sf-card-header sf-gradient-dark">
              <h5 className="sf-card-header-title">
                <i className="bi bi-list-check"></i>
                {selectedCategory ? `[${selectedCategory.name}] 세부 원인` : '대분류를 선택하세요'}
              </h5>
              {selectedCategory && (
                <span className="sf-badge sf-badge-white">{causes.length}개 항목</span>
              )}
            </div>

            <div className="sf-card-body">
              {selectedCategory ? (
                <>
                  {/* 소분류 신규 입력 */}
                  <div className="sf-input-group">
                    <input
                      className="sf-input"
                      type="text"
                      placeholder={`[${selectedCategory.name}] 신규 세부 원인명 입력`}
                      value={newCause.name}
                      onChange={e => setNewCause({ ...newCause, name: e.target.value })}
                      onKeyDown={e => e.key === 'Enter' && handleSaveCause(newCause)}
                    />
                    <button
                      className="sf-btn sf-btn-dark sf-btn-sm"
                      onClick={() => handleSaveCause(newCause)}
                      style={{whiteSpace: 'nowrap'}}
                    >
                      <i className="bi bi-plus-lg"></i> 세부항목 추가
                    </button>
                  </div>

                  {/* 소분류 테이블 */}
                  {causes.length === 0 ? (
                    <div className="sf-empty">
                      <div className="sf-empty-icon">📋</div>
                      <div className="sf-empty-text">등록된 세부 원인이 없습니다. 위에서 추가해 주세요.</div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="sf-table">
                        <thead>
                          <tr>
                            <th style={{width: '60px', textAlign: 'center'}}>No</th>
                            <th>세부 유해위험 원인명</th>
                            <th style={{width: '150px', textAlign: 'center'}}>관리</th>
                          </tr>
                        </thead>
                        <tbody>
                          {causes.map((c, idx) => {
                            const isEditing = editingCauseId === c.id;
                            return (
                              <tr key={c.id}>
                                <td style={{textAlign: 'center'}}>
                                  <span className="sf-row-num">{idx + 1}</span>
                                </td>
                                <td style={{maxWidth: 0}}>
                                  {isEditing ? (
                                    <div className="d-flex align-items-center gap-2">
                                      <input
                                        className="sf-input"
                                        type="text"
                                        value={editCauseName}
                                        onChange={e => setEditCauseName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleConfirmEditCause()}
                                        autoFocus
                                        style={{padding: '6px 10px', fontSize: '0.88rem'}}
                                      />
                                      <button className="sf-btn sf-btn-primary sf-btn-sm" onClick={handleConfirmEditCause} style={{borderRadius: 6, height: 28, padding: '0 12px', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap'}}>
                                        저장
                                      </button>
                                      <button className="sf-btn sf-btn-sm" onClick={() => setEditingCauseId(null)} style={{borderRadius: 6, height: 28, padding: '0 12px', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1'}}>
                                        취소
                                      </button>
                                    </div>
                                  ) : (
                                    <div title={c.name} onDoubleClick={() => handleStartEditCause(c)} style={{fontWeight: 600, color: 'var(--sf-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', cursor: 'pointer'}}>{c.name}</div>
                                  )}
                                </td>
                                <td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                  {!isEditing && (
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <button className="sf-btn sf-btn-sm" onClick={() => handleStartEditCause(c)} style={{ borderRadius: 6, height: 28, display: 'inline-flex', alignItems: 'center', fontWeight: 700, background: '#eff6ff', color: '#2563eb', border: 'none', padding: '0 12px', fontSize: '0.8rem' }}>
                                        수정
                                      </button>
                                      <button className="sf-btn sf-btn-sm" onClick={() => handleDeleteCause(c.id)} style={{ borderRadius: 6, height: 28, display: 'inline-flex', alignItems: 'center', fontWeight: 700, background: '#fef2f2', color: '#ef4444', border: 'none', padding: '0 12px', fontSize: '0.8rem' }}>
                                        삭제
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="sf-empty">
                  <div className="sf-empty-icon">👈</div>
                  <div className="sf-empty-text">좌측에서 대분류를 선택해 주세요.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SafetyHazardCategory;
